import type { DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Field, Icon, Input, SectionHeader } from '@/shared/ui';
import { TextField } from '@/shared/forms';
import { cn } from '@/shared/lib/utils';
import { CountrySelectorField } from '../../../components/CountrySelector';
import type { StationDetailValue } from '../stationFormSchema';
import { addressSummary } from './placeUtils';

const PLACE_LABEL_W = 96;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack form field render-prop API.
type AnyField = any;

export interface PlaceRowProps {
  place: StationDetailValue;
  index: number;
  expanded: boolean;
  dragging: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack form instance; generika tu nepotřebujeme.
  form: any;
  onToggle: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (event: DragEvent) => void;
  onDrop: () => void;
  onSetMain: () => void;
  onShowOnMap: () => void;
  onRemove: () => void;
  onChangeInMap: () => void;
  onCoord: (prop: 'latitude' | 'longitude' | 'zoom', raw: string) => void;
}

/** Řádek nástupního místa — sbalitelný (souhrn + adresa + souřadnice + akce). */
export function PlaceRow({
  place,
  index,
  expanded,
  dragging,
  form,
  onToggle,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onSetMain,
  onShowOnMap,
  onRemove,
  onChangeInMap,
  onCoord,
}: PlaceRowProps) {
  const { t } = useTranslation('products');
  const isMain = index === 0;
  const name = place.description.trim() || t('stationDetail.defaultName');
  const summary = addressSummary(place);

  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={`station-detail-row-${index}`}
      className={cn(
        'border-phx-border-2 rounded-phx overflow-hidden border',
        expanded && 'border-phx-primary',
        dragging && 'opacity-50'
      )}
    >
      {/* Sbalená hlavička */}
      <div
        onClick={onToggle}
        className="px-phx-3 py-phx-2 gap-phx-3 hover:bg-phx-surface-2 flex min-h-12 cursor-pointer items-center"
      >
        <span
          draggable
          onClick={(event) => event.stopPropagation()}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          className="text-phx-text-faint hover:text-phx-text-muted cursor-grab leading-none select-none"
          title={t('stationDetail.dragHandle')}
          aria-label={t('stationDetail.dragHandle')}
          data-testid={`station-detail-drag-${index}`}
        >
          ⠿
        </span>
        <span
          className={cn(
            'text-phx-xs inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-semibold',
            isMain ? 'bg-phx-primary text-white' : 'bg-phx-surface-3 text-phx-text-2'
          )}
        >
          {isMain ? '★' : index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-phx-sm text-phx-text truncate font-semibold">{name}</div>
          {summary ? (
            <div className="text-phx-xs text-phx-text-muted truncate">{summary}</div>
          ) : null}
        </div>
        {isMain ? <Badge tone="primary">{t('stationDetail.mainBadge')}</Badge> : null}
        <Icon
          name={expanded ? 'chevdown' : 'chevright'}
          size={12}
          className="text-phx-text-muted shrink-0"
        />
      </div>

      {/* Rozbalená editace */}
      {expanded ? (
        <div className="bg-phx-surface-2 border-phx-border-2 p-phx-3 gap-phx-3 flex flex-col border-t">
          <form.Field name={`stationDetails[${index}].description`}>
            {(field: AnyField) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={PLACE_LABEL_W}
                label={t('stationDetail.name')}
                required
              />
            )}
          </form.Field>

          <SectionHeader>{t('stationDetail.addressSection')}</SectionHeader>

          <form.Field name={`stationDetails[${index}].street`}>
            {(field: AnyField) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={PLACE_LABEL_W}
                label={t('stationDetail.street')}
              />
            )}
          </form.Field>

          <Field layout="inline" labelWidth={PLACE_LABEL_W} label={t('stationDetail.cpco')}>
            <div className="gap-phx-2 grid grid-cols-[1fr_auto_1fr] items-center">
              <form.Field name={`stationDetails[${index}].descriptiveNumber`}>
                {(field: AnyField) => (
                  <Input
                    value={(field.state.value as string | undefined) ?? ''}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                )}
              </form.Field>
              <span className="text-phx-text-muted">/</span>
              <form.Field name={`stationDetails[${index}].orientationNumber`}>
                {(field: AnyField) => (
                  <Input
                    value={(field.state.value as string | undefined) ?? ''}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                )}
              </form.Field>
            </div>
          </Field>

          <form.Field name={`stationDetails[${index}].city`}>
            {(field: AnyField) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={PLACE_LABEL_W}
                label={t('stationDetail.city')}
              />
            )}
          </form.Field>

          <form.Field name={`stationDetails[${index}].zipCode`}>
            {(field: AnyField) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={PLACE_LABEL_W}
                label={t('stationDetail.zip')}
              />
            )}
          </form.Field>

          <form.Field name={`stationDetails[${index}].poBox`}>
            {(field: AnyField) => (
              <TextField
                field={field}
                layout="inline"
                labelWidth={PLACE_LABEL_W}
                label={t('stationDetail.poBox')}
              />
            )}
          </form.Field>

          <form.Field name={`stationDetails[${index}].country`}>
            {(field: AnyField) => (
              <CountrySelectorField
                field={field}
                mode="single"
                layout="inline"
                labelWidth={PLACE_LABEL_W}
                label={t('stationDetail.country')}
                placeholder={t('stationDetail.countryPlaceholder')}
              />
            )}
          </form.Field>

          <SectionHeader
            action={
              <Button
                type="button"
                variant="outline"
                size="sm"
                iconLeft="pin"
                onClick={onChangeInMap}
              >
                {t('stationDetail.changeInMap')}
              </Button>
            }
          >
            {t('stationDetail.coordsSection')}
          </SectionHeader>

          <div className="gap-phx-2 grid grid-cols-3 items-end">
            <CoordInput
              label={t('stationDetail.lat')}
              value={place.latitude}
              onChange={(raw) => onCoord('latitude', raw)}
            />
            <CoordInput
              label={t('stationDetail.lng')}
              value={place.longitude}
              onChange={(raw) => onCoord('longitude', raw)}
            />
            <CoordInput
              label={t('stationDetail.zoom')}
              value={place.zoom}
              onChange={(raw) => onCoord('zoom', raw)}
            />
          </div>

          <div className="border-phx-border-2 -mx-phx-3 -mb-phx-3 mt-phx-2 px-phx-4 py-phx-3 gap-phx-2 flex items-center border-t">
            {!isMain ? (
              <Button type="button" variant="outline" size="sm" iconLeft="star" onClick={onSetMain}>
                {t('stationDetail.setMain')}
              </Button>
            ) : null}
            <Button type="button" variant="ghost" size="sm" iconLeft="map" onClick={onShowOnMap}>
              {t('stationDetail.showOnMap')}
            </Button>
            <span className="flex-1" />
            <Button
              type="button"
              variant="danger"
              size="icon-sm"
              iconLeft="trash"
              aria-label={t('stationDetail.remove')}
              onClick={onRemove}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CoordInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (raw: string) => void;
}) {
  return (
    <label className="gap-phx-1 flex flex-col">
      <span className="text-phx-xs text-phx-text-muted">{label}</span>
      <Input type="number" value={value ?? ''} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}
